import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { SkuOption } from "@redotech/redo-return-core-common/condition-options";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { SkuMatchType } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { SkuState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { UploadCsvModal } from "./upload-csv-modal";

function matchTypeOptions(
  t: (key: string) => string,
): RadioGroupOption<SkuMatchType>[] {
  return [
    { id: SkuMatchType.ALL, label: t("All products") },
    { id: SkuMatchType.ANY, label: t("Any product") },
    { id: SkuMatchType.NONE, label: t("No product") },
  ];
}

export const SkuDetails = memo(function SkuDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<SkuState>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const stateRef = useRef(state);
  const setStateRef = useRef(setState);

  // Keep refs updated
  useEffect(() => {
    stateRef.current = state;
    setStateRef.current = setState;
  });

  const handleOpenUploadModal = useHandler(() => {
    setUploadModalOpen(true);
  });

  const handleCloseUploadModal = useHandler(() => {
    setUploadModalOpen(false);
  });

  const handleValuesUploaded = useHandler((newCodes: string[]) => {
    const currentState = stateRef.current;
    const existingCodes = currentState.codes || [];
    const uniqueCodes = [...new Set([...existingCodes, ...newCodes])];
    setStateRef.current({ ...currentState, codes: uniqueCodes });
  });

  const skuOptions: RedoListItem<SkuOption>[] = useMemo(
    () =>
      Object.values(SkuOption).map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text:
          option === SkuOption.EQUALS
            ? t("Equals")
            : option === SkuOption.STARTS_WITH
              ? t("Starts with")
              : option === SkuOption.ENDS_WITH
                ? t("Ends with")
                : t("Includes"),
      })),
    [t],
  );

  const selectedOption = state.skuOption
    ? skuOptions.find((option) => option.value === state.skuOption)
    : undefined;

  const tags = useMemo(
    () => state.codes.map((code) => ({ text: code, color: "gray" as const })),
    [state.codes],
  );

  const isFinalizeFlow =
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM;

  const description = t(
    "List of complete or partial SKU codes to check, case-sensitive",
  );

  return (
    <>
      {isFinalizeFlow && (
        <RedoRadioGroup
          disabled={disabled}
          options={matchTypeOptions(t)}
          setValue={(matchType) => setState({ ...state, matchType })}
          size="xs"
          value={state.matchType}
        />
      )}
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Match type")}
        options={skuOptions}
        optionSelected={({ item }) =>
          setState({ ...state, skuOption: item.value })
        }
        placeholder={t("Select match type...")}
        selectedItem={selectedOption}
        size="xs"
      />
      <RedoTagInputField
        delimiters={["newline"]}
        description={description}
        inputValue={inputValue}
        label={t("SKU codes (complete or partial)")}
        placeholder={t("Type and press Enter to add")}
        setInputValue={setInputValue}
        setTags={(codes) => setState({ ...state, codes })}
        size="sm"
        state={disabled ? "disabled" : "default"}
        tags={tags}
      />
      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={UploadIcon}
          onClick={handleOpenUploadModal}
          size="sm"
          type="button"
        >
          {t("Upload CSV")}
        </RedoButton>
      </Flex>
      {uploadModalOpen && (
        <UploadCsvModal
          onClose={handleCloseUploadModal}
          onValuesUploaded={handleValuesUploaded}
          open={uploadModalOpen}
          possibleColumnNames={[
            "sku",
            "sku_code",
            "product_sku",
            "variant_sku",
            "code",
          ]}
          valueLabel={t("SKU codes")}
        />
      )}
    </>
  );
});
